import type { Ref } from 'react';
import type { UIEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import {
  AvailabilityGrid,
  type AvailabilityGridProps,
} from '../../availability/components/availability-grid';
import { Legend, type LegendItem } from '../../availability/components/availability-legend';
import type { CellPopoverLabels } from '../../availability/components/cell-popover-state';
import type {
  AvailabilityGridData,
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellSelection,
  CellState,
  EntriesGridData,
  MultiCellSelection,
} from '../../availability/lib/availability-types';
import {
  AvailabilityGranularitySelect,
  GRANULARITY_COL_WIDTH,
  type Granularity,
} from '../inventory/components/availability-granularity-select';
import { PIN_BELOW_CONTAINER_ATTR } from './pin-below-container-attr';

interface DetailAvailabilityCardProps {
  title: string;
  /** Optional content rendered next to the title — e.g. an `InfoHint` showing when
   * availability data was last updated. */
  titleExtra?: React.ReactNode;
  granularity: Granularity;
  onGranularityChange: (value: Granularity) => void;
  granularityTestId?: string;
  rows: AvailabilityGridRow[];
  periods: AvailabilityPeriod[];
  grid: AvailabilityGridData;
  /** Per-cell entries for window-sourced dashed-border rendering (fromAvailabilityWindow). */
  entriesGrid?: EntriesGridData;
  selection: CellSelection | null;
  popoverLabels: CellPopoverLabels;
  soldAriaLabel: string;
  /** Accessible label for the OPTION-bucket status icon fallback (no customContent/entries). */
  optionAriaLabel?: string;
  /** Accessible label for the merged reserved-bucket status icon fallback (no customContent/entries). */
  reservedAriaLabel?: string;
  legendItems: LegendItem[];
  onCellClick?: AvailabilityGridProps['onCellClick'];
  onClosePopover?: () => void;
  containerRef?: Ref<HTMLDivElement>;
  onScroll?: UIEventHandler<HTMLDivElement>;
  onPrevWindow?: () => void;
  onNextWindow?: () => void;
  /** Drag-range selection callbacks — forwarded to AvailabilityGrid. */
  onCellMouseDown?: AvailabilityGridProps['onCellMouseDown'];
  onCellMouseEnter?: AvailabilityGridProps['onCellMouseEnter'];
  onCellMouseUp?: AvailabilityGridProps['onCellMouseUp'];
  /**
   * When true, suppresses the grid-internal CellPopover on selection (task 4.2 regression fix).
   * The route is responsible for rendering an external CellPopover for occupied cells.
   * Defaults to false.
   */
  disableInternalPopover?: boolean;
  /**
   * Additive opt-in for the shared multi-select SELECT/INSPECT interaction model
   * (task 4.4, `unified-availability-interaction`). When true, the route owns the
   * `multiSelection` state and renders the shared `UnifiedSelectPopover`/`InspectPopover`
   * externally — see `onCellMouseDown`/`onCellMouseEnter`/`onCellMouseUp` above, which the
   * grid repurposes internally for rectangle drag when this is true. Defaults to false.
   */
  multiSelect?: boolean;
  multiSelection?: MultiCellSelection | null;
  onMultiSelectionChange?: (selection: MultiCellSelection | null) => void;
  /**
   * Renders a self-contained popover trigger for non-SELECT-eligible cells (the route's
   * `InspectPopover` wiring) — bypasses the grid's own click/selection handling entirely,
   * since `multiSelect`'s internal click handler ignores non-eligible cells.
   */
  renderCellContent?: (
    row: AvailabilityGridRow,
    period: AvailabilityPeriod,
    cellState: CellState,
    colWidth: number,
  ) => React.ReactNode;
}

export function DetailAvailabilityCard({
  title,
  titleExtra,
  granularity,
  onGranularityChange,
  granularityTestId,
  rows,
  periods,
  grid,
  entriesGrid,
  selection,
  popoverLabels,
  soldAriaLabel,
  optionAriaLabel,
  reservedAriaLabel,
  legendItems,
  onCellClick,
  onClosePopover,
  containerRef,
  onScroll,
  onPrevWindow,
  onNextWindow,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  disableInternalPopover = false,
  multiSelect = false,
  multiSelection = null,
  onMultiSelectionChange,
  renderCellContent,
}: DetailAvailabilityCardProps) {
  const { t } = useTranslation('inventory');

  const navigationControls = (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('availability.prevWindow')}
        data-testid="prev-window-btn"
        disabled={onPrevWindow === undefined}
        onClick={onPrevWindow}
      >
        <ChevronLeft className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </Button>
      <AvailabilityGranularitySelect
        value={granularity}
        onValueChange={onGranularityChange}
        testId={granularityTestId}
      />
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('availability.nextWindow')}
        data-testid="next-window-btn"
        disabled={onNextWindow === undefined}
        onClick={onNextWindow}
      >
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </Button>
    </div>
  );

  return (
    <Card
      data-testid="availability-calendar"
      // Lets `UnifiedSelectPopover`'s pinBelowAnchor case (routes/media-units/$id/index.tsx)
      // find this card via `closest()` from the clicked cell and float below the WHOLE
      // card — title + grid + legend — instead of just the cell, so it never covers the
      // legend row (CU-86cbh0jhy, follow-up). See `PIN_BELOW_CONTAINER_ATTR`'s doc comment.
      {...{ [PIN_BELOW_CONTAINER_ATTR]: '' }}
    >
      <CardHeader className="flex-row flex-wrap items-center gap-2 space-y-0 px-4 py-3">
        <CardTitle className="grow text-[15px]">{title}</CardTitle>
        <div className="flex items-center gap-1 shrink-0">
          {titleExtra}
          {navigationControls}
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0 border-t border-border">
        <AvailabilityGrid
          containerRef={containerRef}
          onScroll={onScroll}
          rows={rows}
          periods={periods}
          grid={grid}
          entriesGrid={entriesGrid}
          selection={selection}
          leftHeaderLabel=""
          showRowHeader={false}
          colWidth={GRANULARITY_COL_WIDTH[granularity]}
          popoverLabels={popoverLabels}
          soldAriaLabel={soldAriaLabel}
          optionAriaLabel={optionAriaLabel}
          reservedAriaLabel={reservedAriaLabel}
          // When disableInternalPopover=true (set by the detail route), the route owns
          // popover rendering (task 4.2 regression fix).
          disableInternalPopover={disableInternalPopover}
          soldCellClickable={onCellClick !== undefined}
          onCellClick={onCellClick}
          onClosePopover={onClosePopover}
          onUnitClick={() => {}}
          onCellMouseDown={onCellMouseDown}
          onCellMouseEnter={onCellMouseEnter}
          onCellMouseUp={onCellMouseUp}
          multiSelect={multiSelect}
          multiSelection={multiSelection}
          onMultiSelectionChange={onMultiSelectionChange}
          renderCellContent={renderCellContent}
        />
        <div className="border-t border-border/40 px-4 py-2" data-testid="availability-legend">
          <Legend items={legendItems} />
        </div>
      </CardContent>
    </Card>
  );
}
